import { useEffect, useState } from 'react';
import { X, Download, Printer, Loader2, FileText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  downloadDocumentPdfFile,
  getDocumentAccess,
  type DocumentAccessDTO,
  type DocumentDTO,
} from '@/services/documents';
import { useModalA11y } from '@/hooks';
import { SafeContent } from './SafeContent';
import { printClinicalDocument } from '@/lib/clinicalDocumentRender';
import { clinicalDocumentLabel, documentDoctorName } from '@/lib/clinicalDocuments';
import { documentStatusLabel } from '@/lib/documentStatus';

interface RecipeDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipe: DocumentDTO | null;
}

export function RecipeDetailModal({
  isOpen,
  onClose,
  recipe,
}: RecipeDetailModalProps) {
  const { modalRef } = useModalA11y({ isOpen, onClose });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const [access, setAccess] = useState<DocumentAccessDTO | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!isOpen || !recipe?.id) {
      setAccess(null);
      setAccessDenied(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoadingAccess(true);
      setAccessDenied(false);
      try {
        const res = await getDocumentAccess(recipe.id);
        if (cancelled) return;
        if (!res.success || !res.data) {
          if (res.status === 403) {
            setAccessDenied(true);
            toast.error('Você não tem permissão para abrir este documento.');
            return;
          }
          // LEGACY sem endpoint novo ainda — continua com conteúdo da lista
          setAccess(null);
          return;
        }
        setAccess(res.data);
      } catch (err: unknown) {
        if (cancelled) return;
        const status = (err as { status?: number })?.status;
        if (status === 403) {
          setAccessDenied(true);
          toast.error('Você não tem permissão para abrir este documento.');
        } else {
          setAccess(null);
        }
      } finally {
        if (!cancelled) setIsLoadingAccess(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, recipe?.id]);

  if (!isOpen || !recipe) return null;

  if (accessDenied) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-6">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-neutral-900"
        >
          <p className="text-center text-gray-800 dark:text-gray-100">
            Acesso negado a este documento.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 w-full rounded-lg bg-teal-600 px-4 py-2.5 font-medium text-white"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  const doctorName = documentDoctorName(recipe);
  const specialty = recipe.appointment?.doctor?.specialty || 'Especialidade';
  const appointmentDate = new Date(recipe.appointment?.date || '');
  const recipeDate = new Date(recipe.createdAt);
  const documentTitle =
    access?.displayName || clinicalDocumentLabel(recipe.documentType);
  const formattedAppointmentDate = isValidDate(appointmentDate)
    ? appointmentDate.toLocaleDateString('pt-BR')
    : 'Não informada';
  const formattedRecipeDate = isValidDate(recipeDate)
    ? recipeDate.toLocaleDateString('pt-BR')
    : 'Não informada';
  const statusLabel =
    access?.statusLabel || documentStatusLabel(recipe) || null;
  const content =
    access?.contentPreview ?? recipe.content ?? '';
  const digitalUrl =
    access?.accessMode === 'DIGITAL_LINK' ? access.accessUrl : recipe.digitalLink || null;

  const handlePrint = () => {
    if (digitalUrl) {
      window.open(digitalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    printClinicalDocument({
      title: documentTitle,
      content,
      doctorName,
      specialty,
      appointmentDate: formattedAppointmentDate,
      documentDate: formattedRecipeDate,
    });
  };

  const handleDownload = async () => {
    if (digitalUrl) {
      window.open(digitalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      setIsDownloading(true);
      const { blob, fileName } = await downloadDocumentPdfFile(recipe.id);
      const url = window.URL.createObjectURL(blob);
      const element = document.createElement('a');
      element.href = url;
      element.download = fileName;
      document.body.appendChild(element);
      element.click();
      element.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('PDF baixado com sucesso.');
    } catch (error) {
      console.error('Erro ao baixar documento em PDF:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Não foi possível baixar o PDF.',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-6">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-recipe-title"
        tabIndex={-1}
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none dark:bg-neutral-900"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-4 sm:px-6 dark:border-neutral-800">
          <div className="flex min-w-0 items-center gap-3">
            <div className="rounded-xl bg-teal-50 p-2.5 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2
                id="modal-recipe-title"
                className="truncate text-lg font-bold text-gray-900 sm:text-xl dark:text-white"
              >
                {documentTitle}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Documento clínico emitido em {formattedRecipeDate}
                {statusLabel ? ` · ${statusLabel}` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar visualização do documento"
            className="rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-neutral-800"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-3xl space-y-5">
            <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm sm:grid-cols-2 dark:border-neutral-700 dark:bg-neutral-800/70">
              <Info label="Médico" value={doctorName} />
              <Info label="Especialidade" value={specialty} />
              <Info label="Data da consulta" value={formattedAppointmentDate} />
              <Info label="Data do documento" value={formattedRecipeDate} />
            </div>

            {isLoadingAccess ? (
              <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin" />
                Preparando documento…
              </div>
            ) : digitalUrl ? (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-7 dark:border-neutral-700 dark:bg-neutral-900">
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                  Esta receita digital está disponível pelo link seguro do App Saúde.
                </p>
                {content ? (
                  <SafeContent
                    content={content}
                    showSkeleton={false}
                    className="mb-4 whitespace-pre-wrap text-[15px] leading-7 text-gray-800 sm:text-base dark:text-gray-100"
                  />
                ) : null}
                <a
                  href={digitalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 font-medium text-white hover:bg-teal-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  Abrir receita digital
                </a>
              </section>
            ) : (
              <section
                aria-label={`Conteúdo do documento: ${documentTitle}`}
                className="min-h-64 rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-7 dark:border-neutral-700 dark:bg-neutral-900"
              >
                <SafeContent
                  content={content}
                  showSkeleton={false}
                  className="whitespace-pre-wrap text-[15px] leading-7 text-gray-800 sm:text-base dark:text-gray-100 [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_li]:my-1 [&_ol]:pl-6 [&_p]:mb-3 [&_ul]:pl-6"
                />
              </section>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6 dark:border-neutral-800 dark:bg-neutral-900">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2.5 font-medium text-gray-700 transition-colors hover:bg-white sm:min-w-28 dark:border-neutral-700 dark:text-gray-300 dark:hover:bg-neutral-800"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 font-medium text-gray-700 transition-colors hover:bg-white sm:min-w-32 dark:border-neutral-700 dark:text-gray-300 dark:hover:bg-neutral-800"
          >
            <Printer className="h-4 w-4" />
            {digitalUrl ? 'Abrir link' : 'Imprimir'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-teal-700 disabled:cursor-wait disabled:opacity-60 sm:min-w-40 dark:hover:bg-teal-500"
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {digitalUrl
              ? 'Abrir receita'
              : isDownloading
                ? 'Gerando PDF...'
                : 'Baixar PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-0.5 break-words font-semibold text-gray-900 dark:text-white">
        {value}
      </p>
    </div>
  );
}

function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}
